import { observable, configure, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

configure({ enforceActions: 'always' });

const EMPTY = { received: [], sent: [] };

// Stevne invitations («Blir du med?» to club mates), backend /api/nsfcompetitioninvites/.
// byCompetition holds one entry per stevne id: invitations to me (to answer) and from me.
class NsfCompetitionInviteStore extends LocalModel {
    constructor() {
        super('nsfCompetitionInvite', {
            namePlural: 'nsfCompetitionInvites',
            namePluralReal: 'nsfcompetitioninvites',
            sort: '-createdDate',
            limit: 100,
            api: {
                search: { url: '/api/nsfcompetitioninvites/', params: {} },
                load: { url: '/api/nsfcompetitioninvites/', params: {} },
                save: { url: '/api/nsfcompetitioninvites/', params: {} },
                delete: { url: '/api/nsfcompetitioninvites/', params: {} },
            },
        });
    }

    @observable nsfCompetitionInvites = [];

    @observable nsfCompetitionInvite = {};

    @observable byCompetition = {};

    @observable sending = false;

    @observable lastResult = null;

    @observable candidates = [];

    @observable searching = false;

    @observable members = [];

    @observable loadingMembers = false;

    summaryFor(id) {
        return (this.byCompetition && this.byCompetition[id]) || EMPTY;
    }

    @action
    async loadFor(ids = []) {
        const unique = [...new Set((ids || []).map(id => parseInt(id, 10)).filter(id => id > 0))];
        if (unique.length === 0) {
            return;
        }
        try {
            const response = await util.fetchApi('/api/nsfcompetitioninvites/', { method: 'GET' }, { ids: unique.join(',') });
            if (response?.status === 200 && response.data) {
                this.updateKeyValue('byCompetition', { ...(this.byCompetition || {}), ...response.data });
            }
        } catch (err) {
            console.error('nsfCompetitionInvite loadFor failed', err);
        }
    }

    /** The club mates shown before any search: the first 25 by last name (never myself). */
    @action
    async loadMembers({ hostname = '', selfId = 0 } = {}) {
        this.updateKeyValue('loadingMembers', true);
        try {
            const response = await util.fetchApi('/api/users/mentions/', { method: 'GET' }, { limit: 25, hostname });
            const rows = (response?.data || []).filter(u => u.id && u.id !== selfId);
            this.updateKeyValue('members', rows);
        } catch (err) {
            console.error('nsfCompetitionInvite loadMembers failed', err);
        } finally {
            this.updateKeyValue('loadingMembers', false);
        }
    }

    /** Club mates matching the text, via the same union scope as @-mentions (never myself). */
    @action
    async searchCandidates(search, { hostname = '', selfId = 0 } = {}) {
        const term = String(search || '').trim();
        if (term.length < 2) {
            this.updateKeyValue('candidates', []);
            return;
        }
        this.updateKeyValue('searching', true);
        try {
            const response = await util.fetchApi('/api/users/mentions/', { method: 'GET' }, { search: term, limit: 10, hostname });
            const rows = (response?.data || []).filter(u => u.id && u.id !== selfId);
            this.updateKeyValue('candidates', rows);
        } catch (err) {
            console.error('nsfCompetitionInvite searchCandidates failed', err);
        } finally {
            this.updateKeyValue('searching', false);
        }
    }

    @action
    clearCandidates() {
        this.updateKeyValue('candidates', []);
    }

    /** Send to the chosen club mates; the per-recipient outcome lands in lastResult. */
    @action
    async send({ nsfCompetitionId, toUserIds = [], message = '' }) {
        this.updateKeyValue('sending', true);
        this.updateKeyValue('lastResult', null);
        try {
            const response = await util.fetchApi('/api/nsfcompetitioninvites/', { method: 'POST' }, {
                nsfCompetitionId, toUserIds, message,
            });
            if (response?.status === 200 || response?.status === 201) {
                this.updateKeyValue('lastResult', response.data || null);
                await this.loadFor([nsfCompetitionId]);
                return response.data;
            }
            this.updateKeyValue('lastResult', { error: response?.message || 'failed' });
        } catch (err) {
            console.error('nsfCompetitionInvite send failed', err);
            this.updateKeyValue('lastResult', { error: String(err && err.message ? err.message : err) });
        } finally {
            this.updateKeyValue('sending', false);
        }
        return null;
    }

    /** «Send påminnelse»: the same POST to everyone still waiting (their row stays 'sent'). */
    @action
    async remind(nsfCompetitionId) {
        const waiting = this.summaryFor(nsfCompetitionId).sent.filter(i => i.status === 'sent').map(i => i.to.id);
        if (waiting.length === 0) {
            return null;
        }
        return this.send({ nsfCompetitionId, toUserIds: waiting, message: '' });
    }

    /** Answer an invitation to me: going / maybe / declined. */
    @action
    async answer(id, status, nsfCompetitionId) {
        try {
            const response = await util.fetchApi(`/api/nsfcompetitioninvites/${id}`, { method: 'PATCH' }, { status });
            if (response?.status === 200) {
                await this.loadFor([nsfCompetitionId || response.data?.nsfCompetitionId]);
                return true;
            }
        } catch (err) {
            console.error('nsfCompetitionInvite answer failed', err);
        }
        return false;
    }
}

const store = new NsfCompetitionInviteStore();
export { NsfCompetitionInviteStore };
export default store;
